<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { split as splitPage } from '@/routes/tools/pdf';
import splitTool from '@/routes/tools/pdf/split';

const { maxMb } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.split.seo',
    fallbackTitleKey: 'tools.pdf.split.title',
    fallbackTitleLabel: 'Split PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: splitPage.url(),
});

const splitDropSublabel = computed(
    () =>
        `${t('tools.pdf.ui.one_pdf_file', 'One PDF file')} · ${t('tools.dropzone.upload_limit_max', 'max')} ${maxMb.value} MB`,
);

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.split.title',
                title: 'Split PDF',
                href: splitPage.url(),
            },
        ],
    },
});

type Mode = 'extract' | 'all';
type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const mode = ref<Mode>('all');
const ranges = ref('');
const pageCount = ref<number | null>(null);
const pageCountLoading = ref(false);
const status = ref<Status>('idle');
const statusMessage = ref('');

const pagesDetectedLine = computed(() => {
    const n = pageCount.value;

    if (n == null) {
        return '';
    }

    if (n === 1) {
        return t('tools.pdf.ui.pages_detected_one', '1 page detected');
    }

    return t('tools.pdf.ui.pages_detected_multi', '{n} pages detected').replace(
        '{n}',
        String(n),
    );
});

async function onFileSelected(files: File[]): Promise<void> {
    file.value = files[0] ?? null;
    pageCount.value = null;
    status.value = 'idle';

    if (!file.value) {
        return;
    }

    pageCountLoading.value = true;
    const formData = new FormData();
    formData.append('file', file.value);

    try {
        const response = await axios.post(splitTool.pageCount.url(), formData);
        pageCount.value = response.data.page_count as number;
    } catch {
        pageCount.value = null;
    } finally {
        pageCountLoading.value = false;
    }
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.split_failed', 'Split failed.')
            );
        } catch {
            return t('tools.pdf.ui.split_failed', 'Split failed.');
        }
    }

    return t('tools.pdf.ui.split_failed', 'Split failed.');
}

async function split(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    if (mode.value === 'extract' && !ranges.value.trim()) {
        return;
    }

    status.value = 'loading';
    statusMessage.value =
        mode.value === 'all'
            ? t('tools.pdf.ui.splitting_all', 'Splitting PDF into pages...')
            : t('tools.pdf.ui.extracting_pages_status', 'Extracting pages...');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('mode', mode.value);

    if (mode.value === 'extract') {
        formData.append('ranges', ranges.value);
    }

    try {
        const response = await axios.post(splitTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const isZip = (response.data as Blob).type === 'application/zip';
        const filename = match?.[1] ?? (isZip ? 'pages.zip' : 'extracted.pdf');

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = isZip
            ? t(
                  'tools.pdf.ui.split_complete_zip',
                  'Split complete! Downloaded all pages as ZIP.',
              )
            : t(
                  'tools.pdf.ui.pages_extracted_success',
                  'Pages extracted successfully!',
              );
        file.value = null;
        pageCount.value = null;
        ranges.value = '';
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-split" />

    <ToolLayout
        :title="t('tools.pdf.split.title', 'Split PDF')"
        :subtitle="t('tools.pdf.split.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :label="
                        t('tools.pdf.ui.drop_pdf', 'Click or drag a PDF here')
                    "
                    :sublabel="splitDropSublabel"
                    @update:model-value="onFileSelected"
                />
                <div v-if="file" class="mt-3">
                    <FileCard
                        :file="file"
                        @remove="
                            () => {
                                file = null;
                                pageCount = null;
                            }
                        "
                    />
                </div>
                <p
                    v-if="pageCountLoading"
                    class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                >
                    {{
                        t(
                            'tools.pdf.ui.reading_page_count',
                            'Reading page count...',
                        )
                    }}
                </p>
                <p
                    v-else-if="pageCount !== null"
                    class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                >
                    {{ pagesDetectedLine }}
                </p>
            </div>

            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <p
                    class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    {{ t('tools.pdf.ui.split_mode_title', 'Split Mode') }}
                </p>
                <div class="flex gap-2">
                    <button
                        type="button"
                        class="flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors"
                        :class="
                            mode === 'all'
                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                        "
                        @click="mode = 'all'"
                    >
                        <span class="block">{{
                            t('tools.pdf.ui.split_all_pages', 'Split all pages')
                        }}</span>
                        <span class="text-xs text-gray-400 dark:text-gray-500">
                            {{
                                t(
                                    'tools.pdf.ui.split_all_pages_hint',
                                    'One PDF per page, downloaded as ZIP',
                                )
                            }}
                        </span>
                    </button>
                    <button
                        type="button"
                        class="flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors"
                        :class="
                            mode === 'extract'
                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                        "
                        @click="mode = 'extract'"
                    >
                        <span class="block">{{
                            t(
                                'tools.pdf.ui.extract_pages_mode',
                                'Extract pages',
                            )
                        }}</span>
                        <span class="text-xs text-gray-400 dark:text-gray-500">
                            {{
                                t(
                                    'tools.pdf.ui.extract_pages_hint',
                                    'Choose specific pages or ranges',
                                )
                            }}
                        </span>
                    </button>
                </div>

                <div v-if="mode === 'extract'" class="mt-4">
                    <label
                        for="ranges"
                        class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {{ t('tools.pdf.ui.page_ranges_label', 'Page ranges') }}
                        <span
                            v-if="pageCount"
                            class="font-normal text-gray-500"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.page_ranges_total',
                                    '(total: {n} pages)',
                                ).replace('{n}', String(pageCount))
                            }}
                        </span>
                    </label>
                    <input
                        id="ranges"
                        v-model="ranges"
                        type="text"
                        :placeholder="
                            t(
                                'tools.pdf.ui.placeholder_page_ranges',
                                'e.g. 1-3, 5, 7-9',
                            )
                        "
                        class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                    />
                    <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
                        {{
                            t(
                                'tools.pdf.ui.page_ranges_hint',
                                'Use commas and hyphens:',
                            )
                        }}
                        <code>1-3, 5, 7-9</code>
                    </p>
                </div>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="
                    !file ||
                    status === 'loading' ||
                    (mode === 'extract' && !ranges.trim())
                "
                @click="split"
            >
                {{
                    mode === 'all'
                        ? t('tools.pdf.ui.split_button_all', 'Split All Pages')
                        : t(
                              'tools.pdf.ui.split_button_extract',
                              'Extract Pages',
                          )
                }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
